import { authModule } from '@/auth/auth-module';
import { getCurrentUserType } from '@/auth/user-type';
import { useClientStore } from '@/client/client-store';

interface UserInformationPayload {
  userEmail: string;
  username: string;
  userAvatar: string;
  selectedClient: string;
  userType: string;
  allClients: string[];
}

interface AppNamePayload {
  appName: string;
  clientGuid: string;
}

export function isAllowedChatbotOrigin(
  origin: string,
  mcpChatUrl: string,
  shellOrigin: string,
): boolean {
  if (origin === shellOrigin) return true;
  try {
    const allowed = new URL(mcpChatUrl).origin;
    return origin === allowed;
  } catch {
    return false;
  }
}

export function buildUserInformationResponse(payload: UserInformationPayload) {
  return { type: 'getUserInformation' as const, ...payload };
}

export function buildAppNameResponse(payload: AppNamePayload) {
  return { type: 'getAppName' as const, ...payload };
}

/**
 * Install a `window` message listener that responds to the chatbot MFE's
 * `getUserInformation` and `getAppName` postMessage requests.
 *
 * Mirrors Angular shell `app.component.ts` lines 64-98.
 * The chatbot MFE (loaded via remoteEntry.js) posts these messages after mount
 * to obtain user context. Without a response it stays on the "Welcome" screen.
 */
export function installChatbotMessageListener(): void {
  window.addEventListener('message', (event: MessageEvent) => {
    if (event.data === 'getUserInformation') {
      const user = authModule.user;
      const { current, available } = useClientStore.getState();
      const userType = getCurrentUserType() || 'external';

      const response = buildUserInformationResponse({
        userEmail: user?.email ?? '',
        username: user?.name ?? '',
        userAvatar: sessionStorage.getItem('userAvatar') ?? '',
        selectedClient: current?.name ?? '',
        userType,
        allClients: available.map((c) => c.name),
      });
      console.log('[shell] getUserInformation event received, responding:', response);

      event.source?.postMessage(response, {
        targetOrigin: event.origin,
      } as WindowPostMessageOptions);
    }

    if (event.data === 'getAppName') {
      const { current } = useClientStore.getState();
      const appName = sessionStorage.getItem('CI') ?? '';

      const response = buildAppNameResponse({
        appName,
        clientGuid: current?.id ?? '',
      });
      console.log('[shell] getAppName event received, responding:', response);

      event.source?.postMessage(response, {
        targetOrigin: event.origin,
      } as WindowPostMessageOptions);
    }
  });
}
